import type { ID, Offset } from '@octopus-do/octopus-project-core'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { useEffect, useMemo, useRef, useState } from 'react'
// @todo review how to update react-draggable or change to custom for updating to latest version of react 19
import type {
  DraggableData,
  DraggableEvent,
  DraggableProps,
} from 'react-draggable'
import { useSelector } from 'react-redux'

import { STICKY_NOTE_OFFSET } from '@/constants/constants'
import { useDragToEdge, useScaleZoom } from '@/hooks'
import { isDragMoved } from '@/utils'
import { findNoteTarget, markNoteTarget, toParentOffset } from '../../../utils'

type UseDragProps = {
  isDragging: boolean
  handleResetDragging: () => void
}

type UseDrag = Partial<DraggableProps> & UseDragProps

const getPointer = (event: DraggableEvent) => {
  const { clientX, clientY } = event as MouseEvent

  return { x: clientX, y: clientY }
}

// A selected note still drags like a page does; only the note being edited
// leaves the mouse to the text.
//
// The page or section under the pointer is what the note will hang on once
// dropped (OCT-5256); it is highlighted on the way, like the page a comment
// would attach to, but by a class rather than the store (NOTE_TARGET_CLASS).
// Over the bare canvas the note goes back to its tab.
const useDrag = (
  id: string,
  parentId: ID,
  disabled: boolean,
  isEditing: boolean
): UseDrag => {
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const nodeRef = useRef<HTMLDivElement>(null)

  const { updateStickyNoteOffset, moveStickyNote } = useProjectLegacyActions()

  const { scale } = useScaleZoom()
  const stickyNotes = useSelector(
    migrationProjectSelector.getProjectStickyNotes
  )
  const tabId = useSelector(migrationProjectSelector.getActiveOrFirstTabId)

  const dragToEdge = useDragToEdge(isDragging)

  const note = useMemo(
    () => stickyNotes.find((item) => item.id === id),
    [stickyNotes]
  )

  const position = note?.offset || STICKY_NOTE_OFFSET

  const target = useRef<ID | null>(null)

  const setTarget = (next: ID | null) => {
    if (next === target.current) {
      return
    }

    markNoteTarget(target.current, next)
    target.current = next
  }

  // Hit-testing reads the layout of every page, and the drag has just moved the
  // note, so doing it on each mouse move makes WebKit lay out a big map over and
  // over. Once per frame, when the frame is laid out anyway, is enough.
  const frame = useRef<number | null>(null)
  const pointer = useRef<Offset | null>(null)

  const trackTarget = (point: Offset) => {
    pointer.current = point

    if (frame.current !== null) {
      return
    }

    frame.current = requestAnimationFrame(() => {
      frame.current = null

      if (pointer.current) {
        setTarget(findNoteTarget(pointer.current))
      }
    })
  }

  const stopTracking = () => {
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current)
    }

    frame.current = null
    pointer.current = null
    setTarget(null)
  }

  useEffect(() => stopTracking, [])

  const onDrag = (event: DraggableEvent, data: DraggableData) => {
    if (disabled || isEditing || !isDragMoved(data, position, scale)) {
      return
    }

    setIsDragging(true)
    trackTarget(getPointer(event))

    dragToEdge(event as React.MouseEvent<HTMLDivElement, MouseEvent>)
  }

  const onStop = (e: DraggableEvent, data: DraggableData) => {
    setIsDragging(false)
    stopTracking()

    // Under the threshold the note snaps back: the position is controlled and
    // stays what the project says.
    if (!note || disabled || !isDragMoved(data, position, scale)) {
      return
    }

    e.stopPropagation()

    const nextParentId = findNoteTarget(getPointer(e)) ?? tabId
    // The note is drawn where the last mouse move left it; the release can
    // come a step further (data.deltaX/Y, in canvas units), and data.x/y below
    // counts that step too.
    const rect = nodeRef.current?.getBoundingClientRect()
    const offset =
      rect &&
      toParentOffset(
        {
          x: rect.left + data.deltaX * scale,
          y: rect.top + data.deltaY * scale,
        },
        nextParentId,
        scale
      )

    if (nextParentId === parentId || !offset) {
      updateStickyNoteOffset({ id: note.id, offset: { x: data.x, y: data.y } })
      return
    }

    moveStickyNote({ id: note.id, nextParentId, offset })
  }

  return {
    isDragging,
    disabled: disabled || isEditing,
    position,
    scale,
    handleResetDragging: () => setIsDragging(false),
    onDrag,
    onStop,
    nodeRef,
  }
}

export default useDrag
